import { createTheme } from '@mui/material/styles';

/**
 * Cardiac-web shared MUI theme — the single source of truth for the app's
 * navy + teal design language. All custom UI (pages, dialogs, the measurement
 * workspace chrome) should pull palette / spacing / radii / typography from here
 * instead of re-declaring hex per component. See WEB/CLAUDE.md → "Design stack".
 *
 * Phase 0 of the MUI migration (Cardiac Vault → "UI Migration — MUI").
 */

// Raw brand tokens — also exported so non-MUI spots (SVG stroke, canvas) can reuse them.
export const cardiacTokens = {
  bg: '#00101f',
  surface: '#0e1c2e',
  surface2: '#16263b',
  well: '#04080d',
  line: '#243652',
  lineStrong: '#314a6b',
  ink: '#e7eef7',
  ink2: '#9fb2cc',
  ink3: '#6a7e9c',
  teal: '#2dd4bf',
  teal2: '#14b8a6',
  sky: '#38bdf8',
  amber: '#f7a35a',
  green: '#4ade80',
  red: '#fb7185',
  violet: '#a78bfa',
} as const;

// Expose the brand tokens on the MUI theme (theme.palette.cardiac.*), typed below.
declare module '@mui/material/styles' {
  interface Palette {
    cardiac: typeof cardiacTokens;
  }
  interface PaletteOptions {
    cardiac?: typeof cardiacTokens;
  }
}

const t = cardiacTokens;

export const cardiacTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: t.teal, dark: t.teal2, contrastText: '#04221d' },
    secondary: { main: t.sky, contrastText: '#04221d' },
    error: { main: t.red },
    warning: { main: t.amber },
    success: { main: t.green },
    info: { main: t.sky },
    background: { default: t.bg, paper: t.surface },
    text: { primary: t.ink, secondary: t.ink2, disabled: t.ink3 },
    divider: t.line,
    cardiac: t,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: "'Hanken Grotesk', system-ui, -apple-system, sans-serif",
    // Serif display face for headings (loaded by the app's existing @font imports).
    h1: { fontFamily: "'Spectral', serif", fontWeight: 600 },
    h2: { fontFamily: "'Spectral', serif", fontWeight: 600 },
    h3: { fontFamily: "'Spectral', serif", fontWeight: 600 },
    h4: { fontFamily: "'Spectral', serif", fontWeight: 600 },
    h5: { fontFamily: "'Spectral', serif", fontWeight: 600 },
    h6: { fontFamily: "'Spectral', serif", fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: t.surface },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundColor: t.surface, border: `1px solid ${t.line}` },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiDialog: {
      styleOverrides: { paper: { backgroundColor: t.surface, border: `1px solid ${t.line}` } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#02080f', border: `1px solid ${t.line}`, fontSize: 11.5 },
        arrow: { color: '#02080f' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: t.line },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.lineStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.teal },
        },
      },
    },
  },
});

export default cardiacTheme;
